import React, { useEffect, useRef } from "react";
import { drawPowerUpToken, getPowerUpVariant } from "../game-canvas/powerup-designs";
import type { PowerUpColor } from "./powerups";
import { asset } from "./portal-env";

// A power-up exactly as it looks on the arena: the same drawPowerUpToken the
// game's renderer uses, with the same design variant, animated on the same
// clock. Used where the help text lists the power-ups, so the picture next to
// each description is the thing you will actually see mid-match. Static under
// prefers-reduced-motion.
const AFFECTS: Record<PowerUpColor, string> = { green: "self", red: "others", blue: "all" };

const iconCache = new Map<string, HTMLImageElement>();
function loadIcon(icon: string): HTMLImageElement {
  const src = asset(`/svg/powerup/${icon}.svg`);
  let img = iconCache.get(src);
  if (!img) { img = new Image(); img.src = src; iconCache.set(src, img); }
  return img;
}

interface Props {
  icon: string; // svg basename under /svg/powerup
  color: PowerUpColor;
  size?: number; // canvas size in CSS px; the token's radius is a bit less so glows fit
}

export default function PowerUpToken({ icon, color, size = 40 }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    c.width = size * dpr;
    c.height = size * dpr;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    const img = loadIcon(icon);
    const variant = getPowerUpVariant();
    const still = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
    const r = size * 0.4; // leave room for the design's rim glow
    const start = performance.now();
    let raf = 0;
    const draw = (now: number) => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, size, size);
      drawPowerUpToken(ctx, size / 2, size / 2, r, img, AFFECTS[color], variant, still ? 0 : (now - start) / 1000);
      const loaded = img.complete && img.naturalWidth > 0;
      // Keep animating (the arena tokens breathe); once loaded and static, stop.
      if (!still || !loaded) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [icon, color, size]);

  return <canvas ref={ref} width={size} height={size} style={{ width: size, height: size, display: "block", flex: "0 0 auto" }} aria-hidden="true" />;
}
